import { CARD_H, CARD_W } from "./constants.js";
import { clamp } from "./numeric.js";
import { cvRound, toU8 } from "./image.js";
import { grayPlane } from "./signature.js";

/* ===========================================================================
   Text regions and OCR preprocessing — cardvision/geometry.py, ocr/base.py.

   The recogniser is PP-OCRv6's CTC model, the same file the Python package runs, so
   everything ahead of it has to arrive at the same pixels: OpenCV's cubic and linear
   resamplers and its CLAHE, ported rather than approximated. A neural net will tolerate
   a slightly different image, but "tolerate" is not a property you can measure, and the
   ported path can be checked against Python crop by crop.
   =========================================================================== */

//: geometry.MODERN_REGIONS / OLD_REGIONS / FULL_ART_REGIONS, as [x0, y0, x1, y1].
export const MODERN_REGIONS = {
  title: [0.05, 0.032, 0.72, 0.1],
  type_line: [0.05, 0.55, 0.8, 0.615],
  // The symbol glyph alone — the old wider band was dominated by card-colour frame
  // pixels and the channel never matched anything (see cardvision/index/symbols.py).
  set_symbol: [0.858, 0.554, 0.94, 0.612],
  collector: [0.03, 0.933, 0.45, 0.961],
  set_info: [0.03, 0.96, 0.55, 0.99],
};
export const OLD_REGIONS = {
  title: [0.05, 0.032, 0.72, 0.1],
  type_line: [0.075, 0.52, 0.8, 0.58],
  set_symbol: [0.858, 0.524, 0.94, 0.578],
  collector: [0.03, 0.95, 0.75, 0.99],
  set_info: [0.03, 0.95, 0.75, 0.99],
};
export const FULL_ART_REGIONS = {
  title: [0.05, 0.032, 0.72, 0.1],
  type_line: [0.05, 0.69, 0.8, 0.755],
  set_symbol: [0.858, 0.694, 0.94, 0.752],
  collector: [0.03, 0.933, 0.45, 0.961],
  set_info: [0.03, 0.96, 0.55, 0.99],
};

//: Frames that actually print a collector number and set code (README 5.4). Reading a
//: number off anything else is reading the copyright line.
export const FRAMES_WITH_COLLECTOR_NUMBER = new Set(["2015", "future"]);

export function regionsFor(frame, fullArt) {
  if (fullArt) return FULL_ART_REGIONS;
  if (frame === "1993" || frame === "1997") return OLD_REGIONS;
  return MODERN_REGIONS;
}

/** geometry.Box.pixels — fractions to a clamped, non-empty pixel rectangle. */
export function boxPixels(box, width = CARD_W, height = CARD_H) {
  const left = clamp(Math.round(box[0] * width), 0, width);
  const top = clamp(Math.round(box[1] * height), 0, height);
  return [
    left,
    top,
    clamp(Math.round(box[2] * width), left + 1, width),
    clamp(Math.round(box[3] * height), top + 1, height),
  ];
}

/**
 * OpenCV's INTER_CUBIC, on a single channel.
 *
 * The A = -0.75 Catmull-Rom variant, sampled at OpenCV's pixel centres
 * (`(d + 0.5) * scale - 0.5`) with replicated borders. Output is rounded to uint8 the
 * same way `resize` does, so it composes with the rest of the ported chain.
 */
export function resizeCubic(src, sw, sh, dw, dh) {
  const A = -0.75;
  const taps = (f) => {
    const c0 = ((A * (f + 1) - 5 * A) * (f + 1) + 8 * A) * (f + 1) - 4 * A;
    const c1 = ((A + 2) * f - (A + 3)) * f * f + 1;
    const c2 = ((A + 2) * (1 - f) - (A + 3)) * (1 - f) * (1 - f) + 1;
    return [c0, c1, c2, 1 - c0 - c1 - c2];
  };
  // Taps that fall outside the source replicate the edge pixel; the *weights* are left
  // alone, which is OpenCV's rule for cubic specifically — it collapses the fraction to
  // zero at the border for linear and explicitly excludes cubic from that. Measured
  // against cv2.resize over the real crops this is also the closest of the four
  // plausible readings, so it is faithful and best-scoring at once.
  const edge = (d, n, m) => {
    const f = (d + 0.5) * (n / m) - 0.5,
      sx = Math.floor(f);
    return [sx, taps(f - sx)];
  };
  const xs = new Int32Array(dw * 4),
    xw = new Float32Array(dw * 4);
  for (let d = 0; d < dw; d++) {
    const [i, c] = edge(d, sw, dw);
    for (let k = 0; k < 4; k++) {
      xs[d * 4 + k] = clamp(i - 1 + k, 0, sw - 1);
      xw[d * 4 + k] = c[k];
    }
  }
  const out = new Float32Array(dw * dh);
  const row = new Float32Array(4);
  for (let d = 0; d < dh; d++) {
    const [i, c] = edge(d, sh, dh);
    for (let k = 0; k < 4; k++) row[k] = clamp(i - 1 + k, 0, sh - 1) * sw;
    for (let x = 0; x < dw; x++) {
      let acc = 0;
      for (let k = 0; k < 4; k++) {
        const base = row[k],
          wy = c[k];
        acc +=
          wy *
          (xw[x * 4] * src[base + xs[x * 4]] +
            xw[x * 4 + 1] * src[base + xs[x * 4 + 1]] +
            xw[x * 4 + 2] * src[base + xs[x * 4 + 2]] +
            xw[x * 4 + 3] * src[base + xs[x * 4 + 3]]);
      }
      out[d * dw + x] = acc;
    }
  }
  return toU8(out);
}

/** OpenCV's INTER_LINEAR, single channel — a plain bilinear sample, no area averaging. */
export function resizeLinear(src, sw, sh, dw, dh) {
  const out = new Float32Array(dw * dh);
  const map = (d, n, m) => {
    const f = (d + 0.5) * (n / m) - 0.5;
    const i = clamp(Math.floor(f), 0, n - 1);
    return [i, Math.min(i + 1, n - 1), clamp(f - i, 0, 1)];
  };
  const cols = Array.from({ length: dw }, (_, x) => map(x, sw, dw));
  for (let y = 0; y < dh; y++) {
    const [y0, y1, fy] = map(y, sh, dh);
    for (let x = 0; x < dw; x++) {
      const [x0, x1, fx] = cols[x];
      const top = src[y0 * sw + x0] * (1 - fx) + src[y0 * sw + x1] * fx;
      const bottom = src[y1 * sw + x0] * (1 - fx) + src[y1 * sw + x1] * fx;
      out[y * dw + x] = top * (1 - fy) + bottom * fy;
    }
  }
  return toU8(out);
}

/**
 * OpenCV's CLAHE — contrast-limited equalisation over an 8 x 8 grid of tiles.
 *
 * Card text crops are small and unevenly lit (foiling, a shadow across the bottom
 * bar), and this is what makes the recogniser's job the same on a phone photo as on a
 * reference render. The clip-and-redistribute arithmetic is integer and matches
 * OpenCV's exactly; the only float step is the bilinear blend between neighbouring
 * tile LUTs, which is what stops the tile grid showing up as blocking.
 */
export function clahe(src, w, h, clipLimit = 2.0, tilesX = 8, tilesY = 8) {
  // OpenCV pads to a whole number of tiles with BORDER_REFLECT_101 before histogramming
  // — and the test is all-or-nothing. If *either* dimension has a remainder, both are
  // extended by `tiles - (size % tiles)`, which for an already-divisible side is a whole
  // extra tile row rather than nothing. Reproducing that is the difference between
  // agreeing with the Python engine and merely resembling it.
  const ragged = w % tilesX !== 0 || h % tilesY !== 0;
  const padX = ragged ? tilesX - (w % tilesX) : 0,
    padY = ragged ? tilesY - (h % tilesY) : 0;
  const pw = w + padX,
    ph = h + padY;
  let lutSrc = src;
  if (ragged) {
    lutSrc = new Float32Array(pw * ph);
    const reflect = (i, n) => (i < n ? i : 2 * n - i - 2);
    for (let y = 0; y < ph; y++) {
      for (let x = 0; x < pw; x++) lutSrc[y * pw + x] = src[reflect(y, h) * w + reflect(x, w)];
    }
  }
  const tw = pw / tilesX,
    th = ph / tilesY,
    area = tw * th;
  const limit = clipLimit > 0 ? Math.max(1, Math.trunc((clipLimit * area) / 256)) : 0;
  const lut = new Uint8Array(tilesX * tilesY * 256);
  const hist = new Int32Array(256);

  for (let ty = 0; ty < tilesY; ty++) {
    for (let tx = 0; tx < tilesX; tx++) {
      hist.fill(0);
      for (let y = ty * th; y < (ty + 1) * th; y++) {
        for (let x = tx * tw; x < (tx + 1) * tw; x++) hist[lutSrc[y * pw + x]]++;
      }
      if (limit > 0) {
        let clipped = 0;
        for (let i = 0; i < 256; i++) {
          if (hist[i] > limit) {
            clipped += hist[i] - limit;
            hist[i] = limit;
          }
        }
        const batch = Math.trunc(clipped / 256);
        let residual = clipped - batch * 256;
        for (let i = 0; i < 256; i++) hist[i] += batch;
        if (residual > 0) {
          const step = Math.max(Math.trunc(256 / residual), 1);
          for (let i = 0; i < 256 && residual > 0; i += step) {
            hist[i]++;
            residual--;
          }
        }
      }
      const scale = 255 / area;
      const base = (ty * tilesX + tx) * 256;
      let sum = 0;
      for (let i = 0; i < 256; i++) {
        sum += hist[i];
        lut[base + i] = clamp(cvRound(sum * scale), 0, 255);
      }
    }
  }

  // Blend the four tile LUTs around each pixel. Tiles are addressed by centre, so the
  // outer half-tile ring clamps onto the edge tiles rather than extrapolating.
  const out = new Float32Array(w * h);
  const cols = new Array(w);
  for (let x = 0; x < w; x++) {
    const f = x / tw - 0.5,
      i = Math.floor(f);
    cols[x] = [Math.max(i, 0), Math.min(i + 1, tilesX - 1), f - i];
  }
  for (let y = 0; y < h; y++) {
    const f = y / th - 0.5,
      i = Math.floor(f);
    const y0 = Math.max(i, 0) * tilesX * 256,
      y1 = Math.min(i + 1, tilesY - 1) * tilesX * 256;
    const ya = f - i,
      ya1 = 1 - ya;
    for (let x = 0; x < w; x++) {
      const [x0, x1, xa] = cols[x],
        xa1 = 1 - xa,
        v = src[y * w + x];
      out[y * w + x] =
        lut[y0 + x0 * 256 + v] * xa1 * ya1 +
        lut[y0 + x1 * 256 + v] * xa * ya1 +
        lut[y1 + x0 * 256 + v] * xa1 * ya +
        lut[y1 + x1 * 256 + v] * xa * ya;
    }
  }
  return toU8(out);
}

//: ocr/base.prepare_line's target height, and rapidocr_engine.LINE_WIDTH.
export const LINE_HEIGHT = 64,
  LINE_WIDTH = 512;
//: The recogniser's own input geometry (rapidocr rec_img_shape [3, 48, 320]). A prepared
//: line is always 512 x 64, so max_wh_ratio is 8 and the width it resizes to is fixed —
//: which is exactly the property the CUDA path needs and the browser gets for free.
export const REC_HEIGHT = 48,
  REC_WIDTH = 384;

/**
 * ocr/base.prepare_line — grayscale, invert light-on-dark, upscale, equalise.
 *
 * Returns the prepared line as uint8 luminance. The three-channel expansion Python does
 * at the end is skipped: the channels are copies of each other, so the tensor builder
 * replicates instead.
 */
export function prepareLine(card, box) {
  const [x0, y0, x1, y1] = boxPixels(box);
  const w = x1 - x0,
    h = y1 - y0;
  let gray = grayPlane(card, CARD_W, CARD_H, x0, y0, x1, y1);
  if (!gray.length) return null;

  let mean = 0;
  for (let i = 0; i < gray.length; i++) mean += gray[i];
  if (mean / gray.length < 110.0) {
    for (let i = 0; i < gray.length; i++) gray[i] = 255 - gray[i];
  }
  const scale = Math.max(1.0, LINE_HEIGHT / Math.max(1, h));
  let width = w,
    height = h;
  if (scale > 1.0) {
    // cv2.resize(fx=, fy=) rounds the size the same way; saturate_cast on a positive
    // double is a round-half-away-from-zero, which is what Math.round does here.
    width = Math.round(w * scale);
    height = Math.round(h * scale);
    gray = resizeCubic(gray, w, h, width, height);
  } else {
    gray = toU8(gray);
  }
  return { data: clahe(gray, width, height), width, height };
}

/**
 * A prepared line as the recogniser's input tensor.
 *
 * rapidocr pads or squeezes to one width, then resizes to the network's 48-pixel height
 * and normalises to [-1, 1]. Both resamples are folded into one linear pass here: the
 * pad-to-512-then-resize-to-384 round trip is a pure scale change on the pixels that
 * exist, plus white where the crop ran out.
 */
export function recogniserTensor(line) {
  const padded = new Float32Array(LINE_WIDTH * line.height).fill(255);
  const keep = Math.min(line.width, LINE_WIDTH);
  if (line.width > LINE_WIDTH) {
    padded.set(resizeLinear(line.data, line.width, line.height, LINE_WIDTH, line.height));
  } else {
    for (let y = 0; y < line.height; y++) {
      for (let x = 0; x < keep; x++) padded[y * LINE_WIDTH + x] = line.data[y * line.width + x];
    }
  }
  const small = resizeLinear(padded, LINE_WIDTH, line.height, REC_WIDTH, REC_HEIGHT);
  const plane = REC_WIDTH * REC_HEIGHT;
  const tensor = new Float32Array(3 * plane);
  for (let i = 0; i < plane; i++) {
    const v = small[i] / 255 - 0.5;
    tensor[i] = tensor[plane + i] = tensor[2 * plane + i] = v / 0.5;
  }
  return tensor;
}

/**
 * CTC greedy decode against the recogniser's own character table.
 *
 * The class list is `["blank", ...charset, " "]` — rapidocr's convention, and the reason
 * the charset is exported from the model's metadata rather than typed out: an off-by-one
 * here produces fluent-looking nonsense rather than an error.
 */
export function ctcDecode(logits, steps, classes, charset) {
  let text = "",
    confidence = 0,
    counted = 0,
    previous = -1;
  for (let t = 0; t < steps; t++) {
    const base = t * classes;
    let best = 0,
      bestValue = logits[base];
    for (let c = 1; c < classes; c++) {
      if (logits[base + c] > bestValue) {
        bestValue = logits[base + c];
        best = c;
      }
    }
    if (best !== previous && best !== 0) {
      text += charset[best] ?? "";
      confidence += bestValue;
      counted++;
    }
    previous = best;
  }
  return { text, confidence: counted ? confidence / counted : 0 };
}
